<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { PageName, TrackingDataProps } from '@/api/tracking';
import noImage from '@/assets/noimage-4to3.jpg';
import { ARTICLE_SHARE_DEBOUNCE_WAIT_MS } from '@/constants';
import { MYPAGE_ROUTE_NAMES } from '@/routers/constants';
import { DgrDialog } from '@stockmarkteam/donguri-ui';
import AdpDocumentCardSkeleton from '@/components/common/adp-document/adp-document-card-skeleton.vue';
import LoadableButton from '@/components/common/atoms/loadable-button.vue';
import MentionAutocompleteTextarea from '@/components/common/molecules/mention-autocomplete-textarea.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import GroupSelect from '@/components/modals/group-select.vue';
import { Article, ArticleMarkSource, Group, UrlPreview } from '@/types';
import { useComment } from '@/utils/actions/comment';
import { useMark } from '@/utils/actions/mark';
import { debounce } from '@/utils/debounce';
import { formatDate } from '@/utils/formatters';
import { isValidHttpUrl } from '@/utils/parsers';
import { userSession } from '@/utils/userSession';
import { useStore } from '@/utils/vue';

const open = defineModel<boolean>('open', { default: false });

const store = useStore();
const route = useRoute();
const router = useRouter();
const { createSnackbar } = useSnackbar();

const pageName: PageName = 'article_share';
const markSource: ArticleMarkSource = 'article_share';

const { createMark, updateMark } = useMark({
  markSource: markSource,
  trackingBaseData: {
    pageName: pageName,
    pageUrl: route.fullPath,
  },
});

const { createComment } = useComment({
  hideSnackbar: true,
  trackingBaseData: {
    pageName: pageName,
    pageUrl: route.fullPath,
  },
});

const trimmedUrl = ref('');
const commentTextRef = ref<InstanceType<typeof MentionAutocompleteTextarea>>();
const urlInput = ref<HTMLInputElement>();

const userInfo = computed(() => store.state.userInfo.userInfo);

const initialFocusEl = () => urlInput.value ?? null;

const isUrlValid = computed(() => {
  return isValidHttpUrl(trimmedUrl.value);
});

const loadingPreview = ref(false);
const hasError = ref(false);
const preview = ref<UrlPreview>({});
const article = reactive({ value: {} as Article });
const isSharing = ref(false);

const initialGroupId = userSession.getLastUsedGroupId();

watch(
  () => trimmedUrl.value,
  debounce(async () => {
    hasError.value = false;
    loadingPreview.value = true;
    preview.value = {};
    if (trimmedUrl.value.length > 0 && isUrlValid.value) {
      try {
        preview.value = await api.fetchUrlPreview(trimmedUrl.value);
      } catch {
        hasError.value = true;
      }
    }
    loadingPreview.value = false;
  }, ARTICLE_SHARE_DEBOUNCE_WAIT_MS),
);

const hasPreview = computed(() => preview.value?.title !== undefined);

const selectedGroup = ref(undefined as Group | undefined);
const setSelectedGroup = (group: Group) => {
  selectedGroup.value = group;
};

const shareEnabled = computed(() => {
  return isUrlValid.value && hasPreview.value && !isSharing.value;
});

const hideDialog = () => {
  if (isSharing.value) return false;
  open.value = false;
  return true;
};

const postMark = async () => {
  const myUserId = userSession.getUserId();
  const existingMark = article.value.marks.find(m => m.user_id === myUserId);
  if (existingMark) {
    await updateMark({
      adpDocument: article.value,
      userInfo: userInfo.value,
    });
  } else {
    await createMark({
      adpDocument: article.value,
      userInfo: userInfo.value,
      hideSnackbar: true,
    });
  }
  store.commit('articleShare/setArticle', article.value);
};

const postComment = async () => {
  if (commentTextRef.value) {
    const comment = commentTextRef.value.trimmedComment;
    const mention = commentTextRef.value.mention;
    if (comment.length > 0) {
      const createdComment = await createComment({
        groupId: selectedGroup.value!.id,
        adpDocument: article.value,
        content: comment,
        mention,
      });
      store.commit('articleShare/setComment', createdComment);
    }
  }
};

const share = async () => {
  if (shareEnabled.value && selectedGroup.value !== undefined) {
    isSharing.value = true;
    const trackingData: TrackingDataProps = {
      pageName: pageName,
      pageUrl: route.fullPath,
    };
    article.value = await api.createArticleShare(
      trimmedUrl.value,
      trackingData,
    );
    await postMark();
    await postComment();

    const groupId = selectedGroup.value.id;
    if (initialGroupId !== groupId) {
      userSession.setLastUsedGroupId(groupId);
    }
    createSnackbar({
      message: '外部リンクを共有しました',
      type: 'success',
    });

    const commentText = commentTextRef.value?.trimmedComment;

    if (commentText !== '') {
      moveToGroupDetail();
    } else {
      moveToUserMark();
    }
  }
  isSharing.value = false;
  hideDialog();
};

// TODO::Featureが取れたら命名を再検討する
const moveToGroupDetail = () => {
  const routePramGroupId = route.params.groupId;
  const routeName = route.name ?? '';
  if (
    routeName === 'groupActivity' &&
    Number(routePramGroupId) === selectedGroup.value!.id
  ) {
    store.commit('articleShare/setIsSharing', true);
  } else {
    router.push({
      name: 'groupActivity',
      params: { groupId: String(selectedGroup.value!.id) },
    });
  }
};

const moveToUserMark = () => {
  const routeName = route.name ?? '';
  if (routeName === MYPAGE_ROUTE_NAMES.marks) {
    store.commit('articleShare/setIsSharing', true);
  } else {
    router.push({
      name: MYPAGE_ROUTE_NAMES.marks,
    });
  }
};

const callPostComment = () => {
  if (commentTextRef.value?.trimmedComment !== '') {
    commentTextRef.value?.postComment();
  } else {
    share();
  }
};
</script>

<template>
  <DgrDialog
    v-model:open="open"
    title="URLを入力して、おすすめの記事を共有しよう"
    :close-interceptor-handler="hideDialog"
    :close-on-escape="false"
    :has-footer="hasPreview"
    :initial-focus-el="initialFocusEl"
  >
    <template #description>
      <div class="m-body">
        <input
          class="m-url-input c-text-regular--m c-textInput"
          ref="urlInput"
          :class="isUrlValid ? 'm-normal-text-area' : 'c-formInput--error'"
          placeholder="https://..."
          v-model.trim="trimmedUrl"
        />
        <div class="m-article-and-comment-container" v-show="shareEnabled">
          <a
            class="m-article-card"
            :href="preview.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div class="m-article-data">
              <div class="m-article-site-name c-title-bold--s">
                {{ preview.media_name }}
              </div>
              <div class="m-article-title c-text-xm">
                {{ preview.title }}
              </div>
              <div class="m-article-site-name c-text-regular--s">
                {{
                  preview.published_at ? formatDate(preview.published_at) : ''
                }}
              </div>
            </div>
            <div class="m-article-image-wrap">
              <img
                :src="preview.image_url"
                @error="preview.image_url = noImage"
              />
            </div>
          </a>
          <div class="o-group-select">
            <GroupSelect
              @selected="setSelectedGroup"
              options-max-height="150px"
              :initial-group-id="initialGroupId"
            ></GroupSelect>
            <div class="o-group-select__text c-text-regular--m">
              にコメントを投稿
            </div>
          </div>
          <MentionAutocompleteTextarea
            class="o-comment-text-area"
            ref="commentTextRef"
            :auto-focus="false"
            :placeholder="'コメントを書く（任意）'"
            :disabled="isSharing"
            @post-comment="share"
          ></MentionAutocompleteTextarea>
        </div>
        <div
          class="o-error c-formBlock__text c-formBlock__text--error"
          v-if="hasError"
        >
          URLから情報を取得できませんでした。再度失敗する場合はお問合せください。
        </div>
        <AdpDocumentCardSkeleton
          class="m-article-card-skeleton"
          v-else-if="!shareEnabled"
          :loading="loadingPreview"
        ></AdpDocumentCardSkeleton>
      </div>
    </template>
    <template #footer>
      <button
        class="m-cancel-button c-btn c-btn--auto c-btnOutline"
        :class="{ disabled: isSharing }"
        :disabled="isSharing"
        @click="hideDialog"
      >
        キャンセル
      </button>
      <LoadableButton
        class="m-share-button c-btn c-btn--auto c-btn--AnewsPrimary"
        :class="{ disabled: !shareEnabled }"
        :disabled="!shareEnabled"
        @click="callPostComment"
        :is-loading="isSharing"
      >
        共有する
      </LoadableButton>
    </template>
  </DgrDialog>
</template>
<style lang="scss" scoped>
.m-body {
  box-sizing: border-box;
  width: 616px;

  .m-url-input {
    width: 100%;
    margin: 0 !important;
  }

  .m-article-card,
  .m-article-card-skeleton {
    border: 1px solid $color-gray400;
    border-radius: 4px;
    margin-top: 8px;
  }
  .m-article-card {
    display: flex;
    flex-direction: row;
    padding: 16px;
    margin-bottom: 8px;
    justify-content: space-between;

    .m-article-image-wrap {
      width: 80px;
      height: 80px;
      img {
        width: 80px;
        height: 80px;
        object-fit: cover;
        object-position: 50% 25%;
        border: 1px solid $color-gray400;
        aspect-ratio: 1/1;
        border-radius: 4px;
      }
    }
    .m-article-data {
      display: flex;
      flex-direction: column;
      .m-article-title {
        font-size: 16px;
        line-height: 22px;
        max-width: 440px;
      }
      .m-article-site-name {
        color: $color-gray600;
        font-size: 12px;
        line-height: 16px;
      }
    }
  }
  .o-group-select {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    .o-group-select__text {
      margin-left: 8px;
    }
  }

  .o-error {
    margin-top: 8px;
  }
}

.m-cancel-button,
.m-share-button {
  min-width: 120px;
  margin-bottom: 0;
}
</style>
